import {
  VehicleChargeGrade,
  VehicleFuelGrade,
  VehicleFuelType,
} from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { DropIcon, ElectricIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import { GasStationState } from "@/domains/gasStation/slice";

type Props = {
  fuelTypes: GasStationState["station"]["fuelTypes"];
  selectedFuelType: VehicleFuelType;
  vehicleFuelType: VehicleFuelType;
  selectedGrade: VehicleFuelGrade | VehicleChargeGrade | null;
  onChange: (
    fuelType: VehicleFuelType,
    grade: VehicleFuelGrade | VehicleChargeGrade | null,
    productId: number,
  ) => void;
};

export default function GasStationFuelType({
  fuelTypes,
  selectedFuelType,
  selectedGrade,
  vehicleFuelType,
  onChange,
}: Props) {
  const { t } = useTranslation("gasStation");

  const values = [];

  const isElectric = vehicleFuelType === VehicleFuelType.Electric;

  if (fuelTypes[VehicleFuelType.Gas] && vehicleFuelType === VehicleFuelType.Gas) {
    [VehicleFuelGrade.Standard, VehicleFuelGrade.Good, VehicleFuelGrade.Excellent].forEach(
      (grade) => {
        values.push({
          productId: fuelTypes[VehicleFuelType.Gas][grade].id,
          type: VehicleFuelType.Gas,
          grade,
        });
      },
    );
  }

  if (fuelTypes[VehicleFuelType.Electric] && vehicleFuelType === VehicleFuelType.Electric) {
    [VehicleChargeGrade.Standard, VehicleChargeGrade.Fast].forEach((grade) => {
      values.push({
        productId: fuelTypes[VehicleFuelType.Electric][grade].id,
        type: VehicleFuelType.Electric,
        grade,
      });
    });
  }

  if (fuelTypes[VehicleFuelType.Diesel] && vehicleFuelType === VehicleFuelType.Diesel) {
    values.push({
      productId: fuelTypes[VehicleFuelType.Diesel].id,
      type: VehicleFuelType.Diesel,
      grade: null,
    });
  }

  if (fuelTypes[VehicleFuelType.GasAvia] && vehicleFuelType === VehicleFuelType.GasAvia) {
    values.push({
      productId: fuelTypes[VehicleFuelType.GasAvia].id,
      type: VehicleFuelType.GasAvia,
      grade: null,
    });
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="text-12 font-medium text-white uppercase">
        {t(isElectric ? "gasStation.typeCharge" : "gasStation.typeFuel")}:
      </div>
      <div
        className="grid gap-1.5 h-[3.75rem]"
        style={{
          gridTemplateColumns: `repeat(${values.length}, 1fr)`,
        }}
      >
        {values
          .filter((item) => !!item.productId)
          .map((item) => {
            const isActive = item.type === selectedFuelType && item.grade === selectedGrade;

            return (
              <Button
                key={`${item.type} - ${item.grade}`}
                variant={isActive ? "solid-gradient" : "solid-outline-gradient"}
                type="button"
                className="h-full text-14 font-bold uppercase p-0 overflow-clip"
                onClick={() => onChange(item.type, item.grade, item.productId)}
              >
                {isElectric ? (
                  <ElectricIcon
                    size={10}
                    className={clsx(
                      "absolute w-2.5 top-2 right-2",
                      isActive ? "text-white" : "text-accent",
                    )}
                  />
                ) : (
                  <DropIcon
                    size={10}
                    className={clsx(
                      "absolute w-2.5 top-2 right-2",
                      isActive ? "text-white" : "text-accent",
                    )}
                  />
                )}

                {t(
                  `gasStation.fuelName.${item.type.toLowerCase()}${item.grade ? `.${item.grade.toLowerCase()}` : ""}`,
                )}
              </Button>
            );
          })}
      </div>
    </div>
  );
}
